import { postJSON } from "../api.js";
import { esc } from "../ui/format.js";
import { uiGet, uiKpis, uiMoney, uiOddsBuild, uiPill, uiRadar } from "../ui/kit.js";
import { toast } from "../ui/modals.js";

/**
 * Meeting planner — replaces app.js's dropdown "Meet a Company" modal.
 *
 * Search the world's largest companies (Global Top 100 first, sector
 * leaders beyond it on request), read a full brief — who is in the room,
 * what they weigh (radar), how the odds are built, agenda, talking points, history — then request it.
 * Data: /api/companies/search, /api/deals/meeting-brief/{name}.
 */

function mpBrief(b) {
  const p = b.profile;
  const v = b.values;
  const rel = b.intel.relationship;
  const fitTone = p && p.fit === "Natural partner" ? "good" : p && p.fit === "Good fit" ? "info" : "warn";
  const health = b.intel.financial_health;
  return `
    <div class="mp-brief-head">
      <div class="sx-eyebrow">${p ? `World #${p.world_rank} · ${esc(p.sector)}` : "Not in the directory"}</div>
      <h3>${esc(b.company)}</h3>
      <p>${esc(p ? p.focus : "No public profile.")}</p>
      <div class="mp-chips">${p ? `${uiPill(p.country)} ${uiPill(uiMoney(p.annual_revenue, { compact: true }) + " revenue")} ${uiPill(p.size_band)} ${uiPill(p.fit, fitTone)}` : ""}
        ${uiPill(health, health === "Under pressure" ? "good" : health === "Stable" ? "" : "warn")}</div>
    </div>
    ${uiKpis([
      { label: "Likely proposal", value: esc(b.odds.deal_type), sub: esc(b.intel.strategic_note) },
      { label: "Chance it closes", value: `${Math.round(b.odds.low * 100)}–${Math.round(b.odds.high * 100)}%` },
      { label: "Value per year", value: `${uiMoney(v.low_min, { compact: true })}–${uiMoney(v.high_max, { compact: true })}`,
        sub: v.cap && v.high_max >= v.cap ? "capped at 5% of their revenue" : `their size ×${v.value_factor}` },
      { label: "Trust", value: `${Math.round(rel.trust_score)}/100`, sub: rel.is_new ? "first contact" : `${rel.interaction_count} past interactions` },
    ])}
    <div class="mp-grid">
      <section class="sx-card">${uiRadar(b.priorities, { caption: "What they weigh in a deal" })}</section>
      <section class="sx-card"><div class="sx-eyebrow" style="margin-bottom:10px;">How the odds are built</div>${uiOddsBuild(b.odds)}
        <p class="mp-note">${esc(b.intel.leverage_note)}</p></section>
    </div>
    <div class="mp-grid">
      <section class="sx-card"><div class="sx-eyebrow">Who is in the room</div>
        <div class="mp-people">${b.attendees.map((a) => `
          <div class="mp-person ${a.side}">
            <div class="mp-avatar">${a.name ? esc(a.name.split(" ").map((x) => x[0]).join("")) : "You"}</div>
            <div><b>${esc(a.name || a.title)}</b>${a.name ? `<span>${esc(a.title)}</span>` : ""}<em>${esc(a.cares_about)}</em></div>
          </div>`).join("")}</div></section>
      <section class="sx-card"><div class="sx-eyebrow">Agenda</div>
        <ol class="mp-agenda">${b.agenda.map((x) => `<li>${esc(x)}</li>`).join("")}</ol>
        <div class="sx-eyebrow" style="margin-top:12px;">Terms likely on the table</div>
        <ul class="mp-list">${b.terms_on_table.map((x) => `<li>${esc(x)}</li>`).join("")}</ul></section>
      <section class="sx-card"><div class="sx-eyebrow">Your talking points</div>
        <ul class="mp-list mp-points">${b.talking_points.map((x) => `<li>${esc(x)}</li>`).join("")}</ul>
        ${b.history.length ? `<div class="sx-eyebrow" style="margin-top:12px;">History with them</div>
          <ul class="mp-list">${b.history.map((h) => `<li><span class="mono">M${h.month}</span> ${esc(String(h.interaction_type || h.type || "").replace(/_/g, " "))}${h.note ? ` — ${esc(h.note)}` : ""}</li>`).join("")}</ul>` : ""}
      </section>
    </div>
    <p class="mp-note">${esc(b.process_note)}</p>`;
}

function mpRow(c, selected) {
  const dot = c.fit === "Natural partner" ? `<i class="mp-dot good" title="Natural partner"></i>`
    : c.fit === "Good fit" ? `<i class="mp-dot info" title="Good fit"></i>` : "";
  return `
    <button type="button" class="mp-row ${selected === c.name ? "on" : ""}" data-name="${esc(c.name)}">
      <span class="mp-rank">#${c.world_rank}</span>
      <span class="mp-row-main"><b>${esc(c.name)}</b><em>${esc(c.sector)} · ${esc(c.country)}</em></span>
      <span class="mp-row-side">${uiMoney(c.annual_revenue, { compact: true })}${dot}${c.met ? `<i class="mp-met">met</i>` : ""}</span>
    </button>`;
}

function openMeetCompanyModal(initialQuery = "") {
  const q0 = typeof initialQuery === "string" ? initialQuery.trim() : "";
  const st = { q: q0, sector: "", country: "", topOnly: !q0, selected: null, rows: [], total: 0 };
  const backdrop = document.createElement("div");
  backdrop.className = "modal-backdrop";
  backdrop.innerHTML = `
    <div class="modal modal-page mp">
      <div class="modal-page-header"><h3>Plan a business meeting</h3>
        <p>The world's largest companies, natural partners for your industry first. Read the brief, then request the meeting: their terms arrive next month.</p></div>
      <div class="mp-body">
        <aside class="mp-side">
          <div class="mp-filters">
            <select id="mp-sector"><option value="">All sectors</option></select>
            <select id="mp-country"><option value="">All countries</option></select>
          </div>
          <label class="mp-top"><input type="checkbox" id="mp-topn" checked/> Global Top 100 only</label>
          <div class="mp-count" id="mp-count"></div>
          <div class="mp-list-wrap" id="mp-list"></div>
        </aside>
        <main class="mp-main" id="mp-brief"><div class="lx-loading">Pick a company to see the brief.</div></main>
      </div>
      <div class="modal-page-footer">
        <div class="lx-footnote" id="mp-foot"></div>
        <div class="modal-actions"><button id="mp-cancel">Close</button><button class="primary" id="mp-go" disabled>Request meeting</button></div>
      </div>
    </div>`;
  document.body.appendChild(backdrop);
  const $m = (s) => backdrop.querySelector(s);
  $m("#mp-cancel").addEventListener("click", () => backdrop.remove());
  if (q0) { $m("#mp-topn").checked = false; }
  let searchSeq = 0, briefSeq = 0, filtersFilled = false;

  const select = async (name) => {
    st.selected = name;
    backdrop.querySelectorAll(".mp-row").forEach((r) => r.classList.toggle("on", r.dataset.name === name));
    const my = ++briefSeq;
    $m("#mp-brief").innerHTML = `<div class="lx-loading">Pulling the brief on ${esc(name)}…</div>`;
    const b = await uiGet(`/deals/meeting-brief/${encodeURIComponent(name)}`);
    if (my !== briefSeq) return;
    $m("#mp-brief").innerHTML = mpBrief(b);
    $m("#mp-brief").scrollTop = 0;
    const go = $m("#mp-go");
    go.disabled = !!b.open_deal_id;
    go.textContent = b.open_deal_id ? "A deal with them is already open" : `Request meeting with ${name}`;
    $m("#mp-foot").textContent = b.profile ? `${b.profile.size_band} · World #${b.profile.world_rank}` : "";
  };

  const search = async (append = false) => {
    const my = ++searchSeq;
    const params = new URLSearchParams({ q: st.q, sector: st.sector, country: st.country, top_only: st.topOnly,
                                         offset: append ? st.rows.length : 0, limit: 40 });
    const res = await uiGet(`/companies/search?${params}`);
    if (my !== searchSeq) return;
    if (!filtersFilled) {
      $m("#mp-sector").insertAdjacentHTML("beforeend", res.sectors.map((s) => `<option>${esc(s)}</option>`).join(""));
      $m("#mp-country").insertAdjacentHTML("beforeend", res.countries.map((s) => `<option>${esc(s)}</option>`).join(""));
      filtersFilled = true;
    }
    st.rows = append ? st.rows.concat(res.results) : res.results;
    st.total = res.total;
    $m("#mp-count").textContent = `${res.total} compan${res.total === 1 ? "y" : "ies"}` +
      (st.topOnly ? ` in the Global Top ${res.top_n}` : ` of ${res.directory_size} in the directory`);
    $m("#mp-list").innerHTML = st.rows.map((c) => mpRow(c, st.selected)).join("") +
      (st.rows.length < st.total ? `<button type="button" class="mp-more" id="mp-more">Show more</button>` : "") +
      (!st.rows.length ? `<div class="lx-foot" style="padding:12px;">No company matches.</div>` : "");
    $m("#mp-more")?.addEventListener("click", () => search(true));
    if (!st.selected && st.rows.length) await select(st.rows[0].name);
  };

  $m("#mp-list").addEventListener("click", (e) => {
    const r = e.target.closest(".mp-row");
    if (r) select(r.dataset.name);
  });
  const refilter = () => { st.selected = null; st.q = ""; search(); };
  $m("#mp-sector").addEventListener("change", (e) => { st.sector = e.target.value; refilter(); });
  $m("#mp-country").addEventListener("change", (e) => { st.country = e.target.value; refilter(); });
  $m("#mp-topn").addEventListener("change", (e) => { st.topOnly = e.target.checked; refilter(); });
  $m("#mp-go").addEventListener("click", async () => {
    if (!st.selected) return;
    const name = st.selected;
    backdrop.remove();
    let res;
    try { res = await postJSON("/deals/meet", { company_name: name }); }
    catch (e) { toast(e.message); return; }
    toast(res.message);
    // Walk straight into the room: the conversation shapes the deal.
    if (typeof window.openConversation === "function") window.openConversation({ kind: "meeting", name });
  });
  return search();
}

// Late-bound: a later view may replace these (window.X = ...), so callers use window.X.
window.openMeetCompanyModal = openMeetCompanyModal;
